import { useEffect, useRef, useState } from "react";
import { Heart, ChatCircle, Share, Gift, MusicNote, Plus, UserPlus, UserCheck, SpeakerHigh, SpeakerSlash, Trash } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import GiftPicker from "./GiftPicker";
import GiftAnimation from "../components/GiftAnimation";

// Module-level mute state — persists across cards for one tab,
// Default UNMUTED — volume is always on until the user explicitly taps to mute.
// We only persist mute state when the USER explicitly chooses; browser-policy fallbacks
// (autoplay blocked => start muted) are NOT persisted, so audio resumes on next interaction.
const MUTE_KEY = "wta:feedMuted";
const readStoredMute = () => {
    try {
        return sessionStorage.getItem(MUTE_KEY) === "1";
    } catch {
        return false;
    }
};
let globalMuted = readStoredMute();
const muteListeners = new Set();
/**
 * Update mute state.
 * @param {boolean} v  new muted value
 * @param {boolean} persist  whether to write to sessionStorage (true = user gesture)
 */
const setGlobalMuted = (v, persist = true) => {
    globalMuted = v;
    if (persist) {
        try { sessionStorage.setItem(MUTE_KEY, v ? "1" : "0"); } catch { /* ignore */ }
    }
    muteListeners.forEach((cb) => cb(v));
};
// On first user gesture anywhere, restore unmuted state if it wasn't user-set.
if (typeof window !== "undefined" && !window.__wtaUnmuteHooked) {
    window.__wtaUnmuteHooked = true;
    const onGesture = () => {
        if (globalMuted && readStoredMute() === false) {
            setGlobalMuted(false, true);
        }
    };
    window.addEventListener("pointerdown", onGesture, { once: true, capture: true });
    window.addEventListener("keydown", onGesture, { once: true, capture: true });
}

export default function VideoCard({ video, onChange }) {
    const { user, refresh } = useAuth();
    const [liked, setLiked] = useState(video.liked);
    const [likes, setLikes] = useState(video.likes_count || 0);
    const [comments, setComments] = useState(video.comments_count || 0);
    const [showComments, setShowComments] = useState(false);
    const [commentList, setCommentList] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [showGift, setShowGift] = useState(false);
    const [following, setFollowing] = useState(false);
    const [muted, setMuted] = useState(globalMuted);
    const videoRef = useRef(null);
    const giftFxRef = useRef(null);

    // Subscribe to global mute changes
    useEffect(() => {
        const cb = (v) => setMuted(v);
        muteListeners.add(cb);
        return () => muteListeners.delete(cb);
    }, []);

    useEffect(() => {
        if (videoRef.current) videoRef.current.muted = muted;
    }, [muted]);

    // Lock background snap scroll while comments are open
    useEffect(() => {
        if (!showComments) return;
        const feed = document.querySelector(".snap-feed");
        const prev = feed?.style.overflow;
        if (feed) feed.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            if (feed) feed.style.overflow = prev || "";
            document.body.style.overflow = "";
        };
    }, [showComments]);

    const author = video.author || {};
    const isOwn = user && author.id === user.id;

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        const tryPlay = async () => {
            try {
                await el.play();
            } catch {
                // Browser blocked unmuted autoplay — fall back to muted but DON'T persist
                // (user hasn't asked to mute; audio will resume on next gesture).
                el.muted = true;
                setGlobalMuted(true, false);
                el.play().catch(() => {});
            }
        };
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    tryPlay();
                } else {
                    el.pause();
                }
            },
            { threshold: 0.6 },
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        if (user && author.id && !isOwn) {
            api.get(`/users/${author.id}/is-following`).then(({ data }) => setFollowing(data.following)).catch(() => {});
        }
    }, [user, author.id, isOwn]);

    const onLike = async () => {
        if (!user) return toast.error("Log in to like");
        try {
            const { data } = await api.post(`/videos/${video.id}/like`);
            setLiked(data.liked);
            setLikes((c) => c + (data.liked ? 1 : -1));
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const loadComments = async () => {
        const { data } = await api.get(`/videos/${video.id}/comments`);
        setCommentList(data);
    };

    const onCommentToggle = async () => {
        if (!showComments) await loadComments();
        setShowComments((s) => !s);
    };

    const onPostComment = async (e) => {
        e.preventDefault();
        if (!user) return toast.error("Log in to comment");
        if (!newComment.trim()) return;
        try {
            const { data } = await api.post("/comments", { video_id: video.id, text: newComment });
            setCommentList((l) => [data, ...l]);
            setComments((c) => c + 1);
            setNewComment("");
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const onFollow = async () => {
        if (!user) return toast.error("Log in to follow");
        try {
            const { data } = await api.post("/follow", { user_id: author.id });
            setFollowing(data.following);
            toast.success(data.following ? `Following ${author.name}` : "Unfollowed");
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const onShare = async () => {
        const url = `${window.location.origin}/feed?v=${video.id}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Link copied");
        } catch {
            toast.success(url);
        }
    };

    const isAdmin = user?.role === "admin";
    const canDelete = isOwn || isAdmin;
    const onDelete = async () => {
        const reason = isAdmin && !isOwn ? "Remove this video from the platform?" : "Delete your video?";
        if (!window.confirm(`${reason} This cannot be undone.`)) return;
        try {
            await api.delete(`/videos/${video.id}`);
            toast.success(isAdmin && !isOwn ? "Video removed" : "Video deleted");
            onChange?.();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const isVideo = (video.video_url || "").match(/\.(mp4|webm|mov|m4v|ogg)(\?.*)?$/i) || video.video_url?.includes("/api/files/");

    return (
        <div className="snap-item relative bg-black flex items-center justify-center">
            {/* Media */}
            {isVideo ? (
                <video
                    ref={videoRef}
                    src={video.video_url}
                    poster={video.thumbnail || undefined}
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                    onClick={() => {
                        const v = videoRef.current;
                        if (!v || !v.src) return;
                        if (v.paused) {
                            v.play().catch(() => { /* autoplay/source error — ignore, browser handled it */ });
                        } else {
                            v.pause();
                        }
                    }}
                    data-testid={`feed-video-${video.id}`}
                />
            ) : (
                <img src={video.thumbnail || video.video_url} alt="" className="w-full h-full object-cover" />
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/30 pointer-events-none" />

            {/* Sound toggle */}
            {isVideo && (
                <button
                    data-testid={`mute-toggle-${video.id}`}
                    onClick={(e) => { e.stopPropagation(); setGlobalMuted(!globalMuted); }}
                    className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-white/20 transition"
                    aria-label={muted ? "Unmute" : "Mute"}
                >
                    {muted ? <SpeakerSlash size={18} weight="fill" color="white" /> : <SpeakerHigh size={18} weight="fill" color="white" />}
                </button>
            )}

            {/* Centered audio hint — "Tap to mute" while audio is on, "Tap to unmute" only as fallback when browser blocked autoplay */}
            {isVideo && (
                <button
                    data-testid={muted ? `tap-to-unmute-${video.id}` : `tap-to-mute-${video.id}`}
                    onClick={(e) => { e.stopPropagation(); setGlobalMuted(!muted); }}
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 px-4 py-2 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 text-white text-xs font-heading uppercase tracking-widest flex items-center gap-2 hover:bg-black/80 transition wta-fade-in ${muted ? "" : "wta-mute-hint-fade"}`}
                >
                    {muted ? <SpeakerSlash size={14} weight="fill" /> : <SpeakerHigh size={14} weight="fill" />}
                    {muted ? "Tap to unmute" : "Tap to mute"}
                </button>
            )}

            {/* Right rail */}
            <div className="absolute right-3 bottom-28 md:bottom-12 z-10 flex flex-col items-center gap-5">
                <Link to={`/profile/${author.id}`} data-testid={`feed-author-${video.id}`} className="relative">
                    <div className="w-12 h-12 rounded-full border-2 border-white overflow-hidden bg-wta-surface2 flex items-center justify-center font-heading text-xl">
                        {author.profile_pic ? <img src={author.profile_pic} alt="" className="w-full h-full object-cover" /> : (author.name?.[0]?.toUpperCase() || "?")}
                    </div>
                    {!isOwn && user && (
                        <button onClick={(e) => { e.preventDefault(); onFollow(); }} data-testid={`follow-btn-${video.id}`} className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-wta-primary flex items-center justify-center border-2 border-black">
                            {following ? <UserCheck size={12} weight="bold" /> : <Plus size={14} weight="bold" />}
                        </button>
                    )}
                </Link>
                <button onClick={onLike} data-testid={`like-btn-${video.id}`} className="flex flex-col items-center gap-1">
                    <Heart size={36} weight={liked ? "fill" : "regular"} color={liked ? "#0EA5E9" : "white"} />
                    <span className="text-xs text-white font-medium">{likes}</span>
                </button>
                <button onClick={onCommentToggle} data-testid={`comment-btn-${video.id}`} className="flex flex-col items-center gap-1">
                    <ChatCircle size={34} weight="regular" color="white" />
                    <span className="text-xs text-white font-medium">{comments}</span>
                </button>
                <button onClick={() => user ? setShowGift(true) : toast.error("Log in to send gifts")} data-testid={`gift-btn-${video.id}`} className="flex flex-col items-center gap-1">
                    <Gift size={34} weight="fill" color="#E4FF00" />
                    <span className="text-xs text-white font-medium">Gift</span>
                </button>
                <button onClick={onShare} data-testid={`share-btn-${video.id}`} className="flex flex-col items-center gap-1">
                    <Share size={34} weight="regular" color="white" />
                    <span className="text-xs text-white font-medium">Share</span>
                </button>
                {canDelete && (
                    <button
                        onClick={onDelete}
                        data-testid={`delete-btn-${video.id}`}
                        className="flex flex-col items-center gap-1 text-red-400 hover:text-red-300"
                        title={isAdmin && !isOwn ? "Remove (admin)" : "Delete"}
                    >
                        <Trash size={32} weight="bold" />
                        <span className="text-[10px] font-medium tracking-wider uppercase">{isAdmin && !isOwn ? "Remove" : "Delete"}</span>
                    </button>
                )}
            </div>

            {/* Bottom info */}
            <div className="absolute left-4 right-20 bottom-24 md:bottom-10 z-10">
                <Link to={`/profile/${author.id}`} className="font-heading text-2xl tracking-wide">@{author.name}</Link>
                {video.caption && <div className="mt-2 text-sm text-white/90">{video.caption}</div>}
                {video.competition_id && (
                    <Link to={`/competitions/${video.competition_id}`} data-testid={`comp-link-${video.id}`} className="inline-flex items-center gap-1 mt-2 text-xs tracking-widest uppercase text-wta-accent border border-wta-accent/40 px-2 py-1 rounded-sm">
                        <MusicNote size={12} /> Competition Entry
                    </Link>
                )}
            </div>

            {/* WTA watermark — drifts vertically so it can't be cropped out easily */}
            <div
                data-testid={`wta-watermark-${video.id}`}
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 z-[5] overflow-hidden"
            >
                <div className="absolute right-3 top-16 md:top-20 wta-watermark-drift flex flex-col items-end gap-1">
                    <img src="/wta-logo.png" alt="" className="w-12 h-12 md:w-14 md:h-14 opacity-70 drop-shadow-lg" />
                    <div className="font-heading text-[9px] tracking-[0.4em] text-white/60 leading-none">
                        @{author.name || "wta"}
                    </div>
                </div>
            </div>

            {/* Comments drawer */}
            {showComments && (
                <div className="fixed inset-0 z-[55] flex items-end" onClick={() => setShowComments(false)}>
                    <div className="absolute inset-0 bg-black/50" />
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-h-[75vh] z-10 bg-wta-surface border-t border-wta-border rounded-t-lg flex flex-col"
                        onWheel={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-4 border-b border-wta-border">
                            <div className="font-heading text-xl tracking-wider">Comments</div>
                            <button onClick={() => setShowComments(false)} data-testid="close-comments" className="text-wta-muted hover:text-white">Close</button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 overscroll-contain">
                            {commentList.length === 0 && <div className="text-wta-muted text-sm text-center py-4">No comments yet</div>}
                            {commentList.map((c) => (
                                <div key={c.id} className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full bg-wta-surface2 border border-wta-border flex items-center justify-center font-heading text-sm overflow-hidden shrink-0">
                                        {c.author?.profile_pic ? <img src={c.author.profile_pic} alt="" className="w-full h-full object-cover" /> : (c.author?.name?.[0]?.toUpperCase() || "?")}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-xs text-wta-muted">@{c.author?.name}</div>
                                        <div className="text-sm break-words">{c.text}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {user && (
                            <form onSubmit={onPostComment} className="p-3 border-t border-wta-border flex gap-2">
                                <input data-testid="comment-input" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment" className="flex-1 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm" />
                                <button data-testid="comment-submit" type="submit" className="btn-primary px-4 rounded-sm text-sm">Post</button>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {showGift && (
                <GiftPicker
                    receiverId={author.id}
                    videoId={video.id}
                    onSent={(gift) => {
                        giftFxRef.current?.play({
                            animation: gift.animation || "float-up",
                            emoji: gift.emoji || "🎁",
                            image_url: gift.image_url || "",
                            color: gift.color || "#F59E0B",
                            name: gift.name,
                            coins: gift.cost,
                            senderName: user?.name,
                        });
                    }}
                    onClose={() => { setShowGift(false); refresh(); onChange?.(); }}
                />
            )}
            <GiftAnimation ref={giftFxRef} />
        </div>
    );
}
